import { Fragment, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Code,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdContentCopy,
  MdDelete,
  MdExpandLess,
  MdExpandMore,
  MdVisibility,
  MdWarningAmber,
} from 'react-icons/md';
import PageLayout from 'components/page-layout';
import PageCard from 'components/page-card';
import EmptyState from 'components/states/EmptyState';
import { spacing } from 'theme/tokens';
import SkeletonTable from 'components/states/SkeletonTable';
import { VenueAgentDto } from '../../models/court-recording/dto/venue-agent.dto';
import { useSelectedOrganization } from '../organization/hooks/useSelectedOrganization';
import ScopePrompt from '../_shared/ScopePrompt';
import { classifyRead } from '../_shared/readState';
import VenueAgentTelemetryPanel from './components/VenueAgentTelemetryPanel';
import { useActivateVenueAgent } from './hooks/useActivateVenueAgent';
import { useCreateVenueAgent } from './hooks/useCreateVenueAgent';
import { useGetOrganizationCourts } from './hooks/useGetOrganizationCourts';
import { useListVenueAgents } from './hooks/useListVenueAgents';
import { useRevokeVenueAgent } from './hooks/useRevokeVenueAgent';
import {
  canActivate,
  courtScopeDiff,
  healthBadge,
  needsActivation,
  recordingBadge,
  relativeTime,
  vt,
} from './utils/venue-agent.utils';

/** List poll while at least one box's panel is open — see useListVenueAgents. */
const EXPANDED_POLL_MS = 15_000;

interface CreatedAgentResult {
  agent: VenueAgentDto;
  rawKey: string;
}

export default function VenueAgentsPage() {
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const toast = useToast();

  const { selectedOrganization } = useSelectedOrganization();
  const orgId = selectedOrganization?.id || '';

  // The 2026-09-12 incident on this page fixed the REFUSED read: a 403 used to
  // render "No venue agents yet" and sent an operator looking for a box that
  // was live all along. The never-issued read was left on the raw `isLoading`,
  // and `useListVenueAgents` is `enabled: Boolean(organizationId)` — a
  // disabled query in React Query v4 reports `isLoading: true` /
  // `fetchStatus: 'idle'`, so with no organization selected this page showed
  // its seven-column skeleton FOREVER. `classifyRead` separates all four.
  // Ids, not a single "expanded id" — an operator comparing two boxes at an
  // install wants both open at once.
  const [expandedAgentIds, setExpandedAgentIds] = useState<string[]>([]);
  // An expanded box is being set up: someone who just pressed "take new
  // photos" is waiting on the box's next heartbeat (~60 s), and a 60 s list
  // poll on top of that reads as "it did nothing". Poll faster only then.
  const { data: agents, isInitialLoading, isError, error } = useListVenueAgents(orgId, {
    refetchIntervalMs: expandedAgentIds.length > 0 ? EXPANDED_POLL_MS : undefined,
  });
  const readState = classifyRead(isError, isInitialLoading, agents);
  const createMutation = useCreateVenueAgent(orgId);
  const revokeMutation = useRevokeVenueAgent(orgId);
  const activateMutation = useActivateVenueAgent(orgId);

  const createModal = useDisclosure();
  const keyModal = useDisclosure();

  const [displayName, setDisplayName] = useState('');
  const [retentionHours, setRetentionHours] = useState(48);
  const [createdResult, setCreatedResult] = useState<CreatedAgentResult | null>(
    null,
  );

  const { courts, isLoading: isLoadingCourts } = useGetOrganizationCourts();

  const toggleExpanded = (agentId: string): void =>
    setExpandedAgentIds((current) =>
      current.includes(agentId)
        ? current.filter((id) => id !== agentId)
        : [...current, agentId],
    );

  const handleCreate = async (): Promise<void> => {
    if (!displayName.trim()) return;
    try {
      const result = await createMutation.mutateAsync({
        displayName: displayName.trim(),
        retentionHours,
      });
      setCreatedResult(result);
      setDisplayName('');
      setRetentionHours(48);
      createModal.onClose();
      keyModal.onOpen();
    } catch (err) {
      toast({
        title: vt('create_failed'),
        description: err instanceof Error ? err.message : String(err),
        status: 'error',
      });
    }
  };

  const handleRevoke = async (agent: VenueAgentDto): Promise<void> => {
    const confirmed = window.confirm(
      vt('revoke_confirm', { name: agent.displayName }),
    );
    if (!confirmed) return;
    try {
      await revokeMutation.mutateAsync(agent.id);
      toast({ title: vt('agent_revoked'), status: 'success' });
    } catch (err) {
      toast({
        title: vt('revoke_failed'),
        description: err instanceof Error ? err.message : String(err),
        status: 'error',
      });
    }
  };

  const handleActivate = async (agent: VenueAgentDto): Promise<void> => {
    // Player-facing and not undoable short of revoking the key, so it gets the
    // same explicit confirm as revoke.
    const confirmed = window.confirm(
      vt('activate_confirm', { name: agent.displayName }),
    );
    if (!confirmed) return;
    try {
      await activateMutation.mutateAsync(agent.id);
      toast({ title: vt('agent_activated'), status: 'success' });
    } catch (err) {
      toast({
        title: vt('activate_failed'),
        description: err instanceof Error ? err.message : String(err),
        status: 'error',
      });
    }
  };

  const handleCopy = (text: string): void => {
    void navigator.clipboard.writeText(text);
    toast({ title: vt('copied'), status: 'success', duration: 1500 });
  };

  const activeAgents = (agents ?? []).filter((a) => !a.revokedAt);

  return (
    <PageLayout>
      <PageCard
        title={vt('title')}
        rightHeader={
          <Button leftIcon={<MdAdd />} colorScheme="brand" onClick={createModal.onOpen}>
            {vt('mint_key')}
          </Button>
        }
      >
        {/*
          The intro carries a COUNT ("… {count} active."), which is a
          definitive scalar about the fleet. It sat above every branch, so it
          said "0 active." for a read that was never issued AND for the refused
          read the 2026-09-12 incident was about — printing a confident number
          directly above the error box that says the list could not be loaded.
          A count is only true of an ANSWERED read (v1.41(c): a missing scalar
          renders as whatever sits beside it, and carries no trace of its own
          absence).
        */}
        {readState === 'answered' && (
          <Text fontSize="sm" color="gray.500" px={spacing.cardHeader} mb={4}>
            {vt('intro', { count: activeAgents.length })}
          </Text>
        )}

        {readState === 'never-issued' ? (
          <ScopePrompt gate="organization" />
        ) : readState === 'loading' ? (
          <SkeletonTable rows={3} columns={8} />
        ) : readState === 'unreadable' ? (
          // A refused or failed read is NOT "no agents". The list route needs an
          // ADMIN membership in the selected org or an elevated (platform)
          // admin account — a COACH, or anyone else without that, gets a 403 —
          // and rendering that as the empty state
          // sent an operator looking for a missing box that was live all along
          // (2026-09-12). Say what the API said.
          //
          // `unreadable`, not the raw `isError`: this query polls every 60s, so
          // keying off `isError` blanked a good fleet table into this alert the
          // first time one background poll failed. A refetch failure that HOLDS
          // rows stays `answered` and the table stands (R96-3).
          <Box
            role="alert"
            data-testid="venue-agents-error"
            borderWidth="1px"
            borderRadius="md"
            p={4}
            mx={spacing.cardHeader}
            mb={4}
          >
            <Text fontWeight="600">{vt('error_title')}</Text>
            <Text fontSize="sm" color="gray.500">
              {error instanceof Error ? error.message : String(error)}
            </Text>
            <Text fontSize="xs" color="gray.500" mt={1}>
              {vt('error_hint')}
            </Text>
          </Box>
        ) : (agents?.length ?? 0) === 0 ? (
          <EmptyState
            title={vt('empty_title')}
            description={vt('empty_description')}
            illustration={<MdVisibility size={64} />}
          />
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th w="1px" />
                <Th>{vt('col_name')}</Th>
                <Th>{vt('col_health')}</Th>
                <Th>{vt('col_recording')}</Th>
                <Th>{vt('col_last_seen')}</Th>
                <Th>{vt('col_clock_skew')}</Th>
                <Th>{vt('col_retention')}</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {(agents ?? []).map((agent) => {
                const h = healthBadge(agent);
                const r = recordingBadge(agent);
                const isExpanded = expandedAgentIds.includes(agent.id);
                const scope = courtScopeDiff(agent);
                return (
                  <Fragment key={agent.id}>
                    <Tr
                      _hover={{ bg: hoverBg }}
                      cursor="pointer"
                      onClick={() => toggleExpanded(agent.id)}
                    >
                      <Td pr={0}>
                        <IconButton
                          aria-label={
                            isExpanded
                              ? vt('hide_telemetry', { name: agent.displayName })
                              : vt('show_telemetry', { name: agent.displayName })
                          }
                          icon={isExpanded ? <MdExpandLess /> : <MdExpandMore />}
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(agent.id);
                          }}
                        />
                      </Td>
                      <Td>
                        <Flex alignItems="center" gap={2}>
                          <Text fontWeight="medium">{agent.displayName}</Text>
                          {scope.hasMismatch && (
                            <Tooltip label={vt('scope_mismatch_tooltip')}>
                              <Box
                                as="span"
                                color="orange.500"
                                display="inline-flex"
                                aria-label={vt('scope_mismatch_aria', { name: agent.displayName })}
                              >
                                <MdWarningAmber />
                              </Box>
                            </Tooltip>
                          )}
                        </Flex>
                        <Text fontSize="xs" color="gray.500" fontFamily="mono">
                          {agent.id.slice(0, 8)}
                        </Text>
                      </Td>
                      <Td>
                        <Tooltip label={h.tooltip}>
                          <Badge colorScheme={h.color}>{h.label}</Badge>
                        </Tooltip>
                      </Td>
                      <Td>
                        {r ? (
                          <Tooltip label={r.tooltip}>
                            <Badge colorScheme={r.color} data-testid={`recording-badge-${agent.id}`}>
                              {r.label}
                            </Badge>
                          </Tooltip>
                        ) : (
                          <Text fontSize="sm">—</Text>
                        )}
                      </Td>
                      <Td>
                        <Text fontSize="sm">{relativeTime(agent.lastSeenAt)}</Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {agent.lastReportedClockSkewSeconds != null
                            ? `${agent.lastReportedClockSkewSeconds}s`
                            : '—'}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm">{agent.retentionHours}h</Text>
                      </Td>
                      <Td>
                        <Flex alignItems="center" gap={1} justifyContent="flex-end">
                          {needsActivation(agent) && (
                            <Tooltip
                              label={
                                canActivate(agent)
                                  ? vt('activate_tooltip')
                                  : vt('activate_disabled_tooltip')
                              }
                              shouldWrapChildren
                            >
                              <Button
                                size="xs"
                                colorScheme="brand"
                                isDisabled={!canActivate(agent)}
                                isLoading={
                                  activateMutation.isLoading &&
                                  activateMutation.variables === agent.id
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleActivate(agent);
                                }}
                              >
                                {vt('activate')}
                              </Button>
                            </Tooltip>
                          )}
                          {!agent.revokedAt && (
                            <Tooltip label={vt('revoke_tooltip')}>
                              <IconButton
                                aria-label={vt('revoke_aria')}
                                icon={<MdDelete />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleRevoke(agent);
                                }}
                              />
                            </Tooltip>
                          )}
                        </Flex>
                      </Td>
                    </Tr>
                    <Tr>
                      <Td colSpan={8} p={0} borderBottomWidth={isExpanded ? undefined : 0}>
                        <Collapse in={isExpanded} animateOpacity unmountOnExit>
                          <VenueAgentTelemetryPanel
                            agent={agent}
                            organizationId={orgId}
                            courts={courts}
                            isLoadingCourts={isLoadingCourts}
                          />
                        </Collapse>
                      </Td>
                    </Tr>
                  </Fragment>
                );
              })}
            </Tbody>
          </Table>
        )}
      </PageCard>

      {/* Mint-key modal */}
      <Modal isOpen={createModal.isOpen} onClose={createModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{vt('mint_modal_title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>{vt('display_name')}</FormLabel>
              <Input
                placeholder={vt('display_name_placeholder')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{vt('retention_label')}</FormLabel>
              <NumberInput
                value={retentionHours}
                min={1}
                max={720}
                onChange={(_, n) => setRetentionHours(Number.isFinite(n) ? n : 48)}
              >
                <NumberInputField />
              </NumberInput>
              <Text fontSize="xs" color="gray.500" mt={1}>
                {vt('retention_hint')}
              </Text>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={2} onClick={createModal.onClose}>
              {vt('cancel')}
            </Button>
            <Button
              colorScheme="brand"
              isLoading={createMutation.isLoading}
              onClick={() => void handleCreate()}
              isDisabled={!displayName.trim()}
            >
              {vt('mint')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Show-key-once modal */}
      <Modal
        isOpen={keyModal.isOpen}
        onClose={() => {
          setCreatedResult(null);
          keyModal.onClose();
        }}
        size="lg"
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{vt('key_modal_title')}</ModalHeader>
          <ModalBody>
            <Text mb={3}>
              {vt('key_modal_body_before')} <Code>recorder/config.json</Code>{' '}
              {vt('key_modal_body_after')}
            </Text>
            {createdResult && (
              <Box>
                <Text fontSize="sm" color="gray.500" mb={1}>
                  {createdResult.agent.displayName}
                </Text>
                <Flex
                  bg="gray.100"
                  p={3}
                  borderRadius="md"
                  alignItems="center"
                  gap={2}
                >
                  <Code
                    flex={1}
                    fontFamily="mono"
                    fontSize="sm"
                    overflowX="auto"
                  >
                    {createdResult.rawKey}
                  </Code>
                  <IconButton
                    aria-label={vt('copy_aria')}
                    icon={<MdContentCopy />}
                    size="sm"
                    onClick={() => handleCopy(createdResult.rawKey)}
                  />
                </Flex>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="brand"
              onClick={() => {
                setCreatedResult(null);
                keyModal.onClose();
              }}
            >
              {vt('saved_it')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageLayout>
  );
}
